import { mergeAttributes, Node } from "@tiptap/core";
import { ReactNodeViewRenderer } from "@tiptap/react";
import { v4 as uuidv4 } from "uuid";
import { generateNodeIds } from "../../utils";
import type { ButtonRowProps } from "./ButtonRow.types";
import { ButtonRowComponentNode } from "./ButtonRowComponent";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    buttonRow: {
      setButtonRow: (props: Partial<ButtonRowProps>) => ReturnType;
    };
  }
}

/**
 * A row carries no colors of its own. The look comes from each button's style, drawn the way
 * the Inbox draws it (see `actionLookFromStyle`), so the canvas matches what gets delivered.
 *
 * These were defaults rather than written values, which made them easy to miss: nothing set
 * them, but every row still had them, and the converter emits whatever a node carries. A
 * default here is the same as writing the color on every button in the product.
 */
export const defaultButtonRowProps: ButtonRowProps = {
  button1Label: "Enter text",
  button1Link: "",
  button1ActionStyle: "button" as const,
  button2Label: "Enter text",
  button2Link: "",
  button2ActionStyle: "secondary" as const,
  padding: 6,
};

export const ButtonRow = Node.create({
  name: "buttonRow",
  group: "block",
  atom: true,
  selectable: false,

  onCreate() {
    generateNodeIds(this.editor, this.name);
  },

  addAttributes() {
    return {
      id: {
        default: () => `node-${uuidv4()}`,
        parseHTML: (element) => element.getAttribute("id"),
        renderHTML: (attributes) => ({ id: attributes.id }),
      },
      button1Label: {
        default: defaultButtonRowProps.button1Label,
        parseHTML: (element) => element.getAttribute("data-button1-label"),
        renderHTML: (attributes) => ({ "data-button1-label": attributes.button1Label }),
      },
      button1Link: {
        default: defaultButtonRowProps.button1Link,
        parseHTML: (element) => element.getAttribute("data-button1-link"),
        renderHTML: (attributes) => ({ "data-button1-link": attributes.button1Link }),
      },
      button1ActionStyle: {
        default: defaultButtonRowProps.button1ActionStyle,
        parseHTML: (element) => element.getAttribute("data-button1-action-style"),
        renderHTML: (attributes) => ({
          "data-button1-action-style": attributes.button1ActionStyle,
        }),
      },
      button1BackgroundColor: {
        default: undefined,
        parseHTML: (element) => element.getAttribute("data-button1-bg"),
        renderHTML: (attributes) => ({ "data-button1-bg": attributes.button1BackgroundColor }),
      },
      button1TextColor: {
        default: undefined,
        parseHTML: (element) => element.getAttribute("data-button1-color"),
        renderHTML: (attributes) => ({ "data-button1-color": attributes.button1TextColor }),
      },
      button1If: {
        default: undefined,
        parseHTML: () => undefined,
        renderHTML: () => ({}),
      },
      button1Locales: {
        default: undefined,
        parseHTML: () => undefined,
        renderHTML: () => ({}),
      },
      button2Label: {
        default: defaultButtonRowProps.button2Label,
        parseHTML: (element) => element.getAttribute("data-button2-label"),
        renderHTML: (attributes) => ({ "data-button2-label": attributes.button2Label }),
      },
      button2Link: {
        default: defaultButtonRowProps.button2Link,
        parseHTML: (element) => element.getAttribute("data-button2-link"),
        renderHTML: (attributes) => ({ "data-button2-link": attributes.button2Link }),
      },
      button2ActionStyle: {
        default: defaultButtonRowProps.button2ActionStyle,
        parseHTML: (element) => element.getAttribute("data-button2-action-style"),
        renderHTML: (attributes) => ({
          "data-button2-action-style": attributes.button2ActionStyle,
        }),
      },
      button2BackgroundColor: {
        default: undefined,
        parseHTML: (element) => element.getAttribute("data-button2-bg"),
        renderHTML: (attributes) => ({ "data-button2-bg": attributes.button2BackgroundColor }),
      },
      button2TextColor: {
        default: undefined,
        parseHTML: (element) => element.getAttribute("data-button2-color"),
        renderHTML: (attributes) => ({ "data-button2-color": attributes.button2TextColor }),
      },
      button2If: {
        default: undefined,
        parseHTML: () => undefined,
        renderHTML: () => ({}),
      },
      button2Locales: {
        default: undefined,
        parseHTML: () => undefined,
        renderHTML: () => ({}),
      },
      padding: {
        default: defaultButtonRowProps.padding,
        parseHTML: (element) => parseInt(element.getAttribute("data-padding") || "6"),
        renderHTML: (attributes) => ({ "data-padding": attributes.padding }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-type="buttonRow"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["div", mergeAttributes(HTMLAttributes, { "data-type": "buttonRow" })];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ButtonRowComponentNode, {
      stopEvent: ({ event }) => {
        // Allow drag events to propagate
        if (event.type === "dragstart" || event.type === "drop") {
          return false;
        }
        // Stop propagation for other events to allow contentEditable focus
        return true;
      },
    });
  },

  addCommands() {
    return {
      setButtonRow:
        (props) =>
        ({ chain }) => {
          return chain()
            .insertContent({
              type: this.name,
              attrs: { ...defaultButtonRowProps, ...props },
            })
            .run();
        },
    };
  },
});
